import React, { forwardRef, useCallback, useState } from 'react'
import { cn, Dialog, MembershipBadge, RectangleButton } from '@yanolja-rnd/nol-design-system'
import { IcInfo } from '@yanolja-rnd/nol-icons'
import { toKRW } from '~/api/utils/krNumberToString'
import dynamic from 'next/dynamic'
import useModalHash from '~/app/components/common/useModalHash'
import CartLogin from '~/app/components-nds/domestic/cart/cart-login'
import useDomesticCartStore from '~/app/store/domestic/cart/cart'
import useDomesticCartPrice from '~/app/components-nds/domestic/cart/hooks/use-domestic-cart-price'
import {
  useDomesticCartPurchaseMutation,
  useDomesticCartQuery,
} from '~/app/react-query/domestic/cart'
import { TCommonViewer } from '~/api/routes/common/type'
import cookies from 'browser-cookies'
import { useRouter } from 'next/router'
import { getYaNative } from '@yanolja-rnd/ya-native'
import { isNative } from '~/app/utils/typeguards'
import qs from 'qs'
import { DomesticCartAlertAction } from '~/api/routes/domestic/cart/controller/type'
import { useLogin } from '~/app/utils/useLogin'
import { useLoading } from '~/app/components/common/LoadingProvider'
import GoldClassBenefitsBottomSheet from '~/app/components-nds/common/gold-class-benefits-bottom-sheet'

const InfoListBottomSheet = dynamic(
  () => import('~/app/components-nds/common/info-list-bottom-sheet').then(module => module.default),
  { ssr: false }
)

const goToOrderPage = (querystring: string) => {
  const yaNative = getYaNative()
  if (isNative(yaNative)) {
    yaNative.wv({
      url: `${window.location.origin}/orders/new`,
      ...(querystring && { params: qs.parse(querystring, { depth: 0 }) as any }),
    })
  } else {
    window.location.href = `/orders/new?${querystring}`
  }
}

interface ErrorData {
  title: string
  description: string
  action: string
  confirmButtonText: string
}

interface PurchaseCTAButtonProps {
  isMember: boolean
  viewer: TCommonViewer
  expectedPayAmount: number
  className?: string
}

interface GoldClassBenefitsBottomSheetInfo {
  purchaseQueryString: string
  discountPrice: number
  isOpen: boolean
}

const PurchaseCTAButton = forwardRef<HTMLDivElement, PurchaseCTAButtonProps>(
  ({ isMember, viewer, expectedPayAmount, className }, ref) => {
    const router = useRouter()

    const [isPaymentAmountBottomSheetOpen, setIsPaymentAmountBottomSheetOpen] = useModalHash({
      id: 'cart-payment-expected-amount',
    })
    const [isErrorDialogOpen, setIsErrorDialogOpen] = useModalHash({
      id: 'cart-order-error-dialog',
    })

    /**
     * useQuery 의 isFetching을 사용하지 않는 이유: 다른 컴포넌트에서 refetch 될때도 isFetching이 true가 되어서 로딩 상태가 유지되는 문제가 있음
     * 따라서 직접 refetch 될때만 isRefetching을 true로 설정하고 완료되면 false로 설정함
     */
    const [isRefetching, setIsRefetching] = useState(false)

    // history API와 연동되서 노출되는 dialog가 아니기에 useModalHash 사용하지 않음
    const [goldClassBenefitsBottomSheetInfo, setGoldClassBenefitsBottomSheetInfo] =
      useState<GoldClassBenefitsBottomSheetInfo>({
        purchaseQueryString: '',
        discountPrice: 0,
        isOpen: false,
      })

    const [errorData, setErrorData] = useState<ErrorData | null>(null)

    const { mutateAsync: purchaseDomesticCart, isPending: isPurchaseLoading } =
      useDomesticCartPurchaseMutation()
    const { refetch } = useDomesticCartQuery()

    const { onLogin } = useLogin()

    useLoading(isPurchaseLoading || isRefetching)

    const handleRefetch = async () => {
      if (isRefetching) return // 중첩 호출 방지
      setIsRefetching(true)
      try {
        await refetch()
      } finally {
        setIsRefetching(false)
      }
    }

    const onClickAlert = useCallback(() => {
      if (!errorData) return

      switch (errorData?.action) {
        case DomesticCartAlertAction.DISMISS:
          const yaNative = getYaNative()
          if (isNative(yaNative)) {
            yaNative.dismiss()
          } else {
            window.history.go(-1)
          }
          break
        case DomesticCartAlertAction.REFRESH:
          handleRefetch()
          setIsErrorDialogOpen(false)
          break
        case DomesticCartAlertAction.LOGIN:
          onLogin()
          break
      }
    }, [errorData, handleRefetch, onLogin])

    const { selectedOptionCount, selectedOptionIdsToArray } = useDomesticCartStore(
      ({ selectedOptionIds }) => {
        return {
          selectedOptionIdsToArray: Array.from(selectedOptionIds),
          selectedOptionCount: selectedOptionIds.size,
        }
      }
    )

    const { totalMemberDiscountPrice, selectedCartOptions } = useDomesticCartPrice()

    const isDisabled = selectedOptionCount === 0

    const onClickPurchase = async () => {
      const trackCode =
        (router.query.trackCode as string) ||
        cookies.get('trackcode') ||
        cookies.get('trackCode') ||
        ''

      // 카테고리별 수량 계산
      const count = selectedCartOptions.reduce(
        (acc, cur) => {
          switch (cur.type) {
            case 'DOMESTIC_RESERVATION':
              acc.property++
              break
            case 'DOMESTIC_LEISURE':
              acc.leisure++
              break
            case 'DOMESTIC_TRAIN':
              acc.train++
              break
            case 'DOMESTIC_BUS':
              acc.bus++
              break
            default:
              break
          }
          return acc
        },
        {
          property: 0,
          leisure: 0,
          train: 0,
          bus: 0,
        }
      )

      try {
        const { success, discountPriceByMemberClass, querystring } = await purchaseDomesticCart({
          viewer: {
            role: viewer.role,
            hashedMemberNo: viewer.hashedMemberNo,
          },
          trackCode,
          count,
          optionIds: selectedOptionIdsToArray,
          lng: router.query.lng as string,
          lat: router.query.lat as string,
        })

        if (success) {
          if (!!discountPriceByMemberClass) {
            setGoldClassBenefitsBottomSheetInfo({
              purchaseQueryString: querystring,
              isOpen: true,
              discountPrice: discountPriceByMemberClass,
            })
            return
          }

          if (querystring) {
            goToOrderPage(querystring)
          }
        }
      } catch (error) {
        if (error.response.data.data) {
          setErrorData(error.response.data.data?.alert)
          setIsErrorDialogOpen(true)
        }
      }
    }

    return (
      <div className={cn('bg-static-white px-16', className)} ref={ref}>
        <div className="flex items-center justify-between">
          <span className="typography-body-14-bold">총 {selectedOptionCount}건</span>
          <div className="flex items-center gap-6 typography-body-13-regular">
            <button
              type="button"
              onClick={() => setIsPaymentAmountBottomSheetOpen(true)}
              className="flex"
            >
              <span className="mr-2">결제 예상 금액</span>
              <IcInfo height={16} width={16} />
            </button>
            <span className="typography-subtitle-18-bold">{toKRW(expectedPayAmount)}</span>
          </div>
        </div>

        {/* Gold 특가 뱃지 */}
        {!!totalMemberDiscountPrice && (
          <div className="mt-8 flex justify-end">
            <MembershipBadge
              type="gold_special_price"
              size={{ pc: 'large', mobile: 'large' }}
              className="typography-caption-11-bold"
            >
              특가{' '}
              <span className="text-static-white">
                {toKRW(totalMemberDiscountPrice)} 할인받았어요.
              </span>
            </MembershipBadge>
          </div>
        )}

        <RectangleButton
          className="mt-16 w-full"
          theme="filled_primary"
          size="large"
          weight="bold"
          disabled={isDisabled || isPurchaseLoading || isRefetching}
          onClick={onClickPurchase}
        >
          구매하기
        </RectangleButton>

        {!isMember && <CartLogin className="mt-12" />}

        <InfoListBottomSheet
          isOpen={isPaymentAmountBottomSheetOpen}
          onClose={() => setIsPaymentAmountBottomSheetOpen(false)}
          header="결제 예상 금액"
          contents={[
            {
              subContents: [{ content: '최대 할인이 적용된 금액이에요.' }],
            },
            {
              subContents: [
                {
                  content:
                    '실제 결제 금액은 선착순 쿠폰의 잔여 수량 및 기차 묶음할인 금액 변동에 따라 달라질 수 있어요.',
                },
              ],
            },
          ]}
        />

        {isErrorDialogOpen && errorData && (
          <Dialog
            id="cart-order-error-dialog"
            show={isErrorDialogOpen}
            title={errorData.title}
            description={errorData.description}
            positiveButton={{
              text: errorData.confirmButtonText,
              onClick: onClickAlert,
            }}
          />
        )}

        <GoldClassBenefitsBottomSheet
          isOpen={goldClassBenefitsBottomSheetInfo.isOpen}
          onClickContinuePurchaseButton={() => {
            goToOrderPage(goldClassBenefitsBottomSheetInfo.purchaseQueryString)
          }}
          onClose={() =>
            setGoldClassBenefitsBottomSheetInfo({
              purchaseQueryString: '',
              discountPrice: 0,
              isOpen: false,
            })
          }
          vipDiscountPriceText={toKRW(goldClassBenefitsBottomSheetInfo.discountPrice)}
        />
      </div>
    )
  }
)

export default PurchaseCTAButton
